import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { canSee } from "@/lib/access";

/**
 * Route gate.
 *
 *   • `role="owner"`      → owner or super-admin only.
 *   • `role="app"`        → anyone signed in with role=employee.
 *   • `role="marketplace"`→ owners **or** employees with
 *                           `can_manage_marketplace = true` (used for
 *                           `/dashboard`, Marketplace, Orders, Shipping,
 *                           Branding — the catalog-management surfaces).
 *
 * Anything more restrictive is layered as an extra ProtectedRoute wrapper
 * around the child route (e.g. `role="owner"` around Employees).
 */
export default function ProtectedRoute({ children, role, needs }) {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--app-bg)]">
        <div className="text-[var(--ink-muted)] text-sm mono">Loading DialFlow…</div>
      </div>
    );
  }
  if (user === false) return <Navigate to="/login" replace />;

  const isOwner = user.role === "owner";
  const isMarketplaceEmp = user.role === "employee" && !!user.can_manage_marketplace;

  const allowed = (() => {
    if (!role) return true;
    if (role === "marketplace") return isOwner || isMarketplaceEmp;
    return user.role === role;
  })();

  if (!allowed) {
    // Send everyone to their natural home. Marketplace-permitted employees
    // still default to /app; they can jump into /dashboard/marketplace via
    // the sidebar link there.
    if (user.role === "employee") return <Navigate to="/app" replace />;
    return <Navigate to={isOwner ? "/dashboard" : "/app"} replace />;
  }
  // Pages hidden from the menu for this account (see lib/access.js).
  if (needs && !canSee(user, needs)) return <Navigate to="/dashboard" replace />;
  return children;
}
